import { onBeforeUnmount, onMounted } from 'vue';

// What the cheat-sheet dialog lists. `keys` are shown as-is; the modifier is
// written "Mod" and rendered as ⌘ or Ctrl by the dialog.
export const SHORTCUTS = [
  { id: 'DELETE', keys: ['Delete'] },
  { id: 'UNDO', keys: ['Mod', 'Z'] },
  { id: 'REDO', keys: ['Mod', 'Shift', 'Z'] },
  { id: 'DUPLICATE', keys: ['Mod', 'D'] },
  { id: 'COPY', keys: ['Mod', 'C'] },
  { id: 'PASTE', keys: ['Mod', 'V'] },
  { id: 'SELECT_ALL', keys: ['Mod', 'A'] },
  { id: 'PAN', keys: ['Space', 'Drag'] },
  { id: 'FIT', keys: ['Shift', '1'] },
  { id: 'ZOOM_RESET', keys: ['Mod', '0'] },
  { id: 'SAVE', keys: ['Mod', 'S'] },
  { id: 'ESCAPE', keys: ['Esc'] },
  { id: 'HELP', keys: ['?'] },
];

const isTyping = target => {
  if (!target) return false;
  const { tagName, isContentEditable } = target;
  if (
    tagName === 'INPUT' ||
    tagName === 'TEXTAREA' ||
    tagName === 'SELECT' ||
    tagName === 'BUTTON' ||
    isContentEditable
  ) {
    return true;
  }
  if (typeof target.closest !== 'function') return false;
  return Boolean(
    target.closest(
      'dialog, [role="combobox"], [role="listbox"], [role="dialog"]'
    )
  );
};

/**
 * Registers the builder's keyboard shortcuts on window for the lifetime of
 * the calling component. Everything is ignored while the user is typing in a
 * field (or the code editor), except Escape.
 *
 * @param {Object} handlers one callback per action (see the switch below)
 */
export function useCsmlShortcuts(handlers) {
  const onKeyDown = event => {
    if (event.key === 'Escape') {
      handlers.escape?.();
      return;
    }
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();

    // Saving is the one shortcut that should work from inside a field too.
    if (isTyping(event.target) && !(mod && key === 's')) return;

    if (mod) {
      const actions = {
        z: event.shiftKey ? handlers.redo : handlers.undo,
        y: handlers.redo,
        d: handlers.duplicate,
        c: handlers.copy,
        v: handlers.paste,
        a: handlers.selectAll,
        s: handlers.save,
        0: handlers.resetZoom,
      };
      const action = actions[key];
      if (!action) return;
      event.preventDefault();
      action();
      return;
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      handlers.remove?.();
    } else if (
      event.key === '!' ||
      (event.shiftKey && event.code === 'Digit1')
    ) {
      handlers.fit?.();
    } else if (event.key === '?') {
      handlers.help?.();
    } else if (event.code === 'Space' && !event.repeat) {
      event.preventDefault();
      handlers.panStart?.();
    }
  };

  const onKeyUp = event => {
    if (event.code === 'Space') handlers.panEnd?.();
  };

  onMounted(() => {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  });
}
